import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Backdrop from "@mui/material/Backdrop";
import FamilyHeader from "./FamilyHeader";
import FamilyChecklistDatePickerDialog from "./FamilyChecklistDatePickerDialog";
import { shouldUseCustomDatePicker } from "../../common/utils/webView";
import { getSessionContext } from "../../common/utils/sessionContext";
import { getCheckListOfFamilyMember } from "../../common/api/familyportalApi";
import { IoChevronBack, IoCalendarOutline, IoClose } from "react-icons/io5";
import { FaCheckCircle, FaMinusCircle, FaTimesCircle } from "react-icons/fa";

export const formatDateKey = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const getTodayDateKey = () => formatDateKey(new Date());

export const formatDateLabel = (dateKey, { isToday = false } = {}) => {
  if (isToday) {
    return "Today";
  }
  try {
    const [y, m, d] = dateKey.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dateKey;
  }
};

const CHECKLIST_SECTIONS = [
  { id: "completed", label: "Completed" },
  { id: "notAvailable", label: "Not Available" },
  { id: "declined", label: "Declined" },
  { id: "notDone", label: "Not Done" },
];

const formatChecklistTime = (updatedAt) => {
  if (!updatedAt) return "";

  const parts = updatedAt.split(" ");
  return `${parts[1]?.slice(0, 5)} ${parts[2]}`;
};

const normalizeChecklistItem = (item) => ({
  id: item.checklistId,
  task: item.title || "",
  description: item.description || "",
  reason: item.reason || "",
  caregiverName: item.caregiverName || "",
  time: formatChecklistTime(item.updatedAt),
  status: String(item.status || "").trim(),
});

const resolveStatusSection = (status) => {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();

  if (normalized === "completed") {
    return "completed";
  }
  if (normalized === "not available") {
    return "notAvailable";
  }
  if (normalized === "declined") {
    return "declined";
  }
  if (normalized === "to-do" || normalized === "todo" || normalized === "not done" || normalized === "not completed") {
    return "notDone";
  }

  return "notDone";
};

const mapChecklistApiResponse = (apiData, selectedDateKey) => {
  const todayKey = getTodayDateKey();
  const isToday = selectedDateKey === todayKey;
  const checklists = Array.isArray(apiData?.checklists) ? apiData.checklists.map(normalizeChecklistItem) : [];

  const grouped = {
    completed: [],
    notAvailable: [],
    declined: [],
    notDone: [],
  };

  checklists.forEach((item) => {
    const sectionId = resolveStatusSection(item.status);
    grouped[sectionId].push(item);
  });

  const summary = {
    total: checklists.length,
    completed: grouped.completed.length,
    notAvailable: grouped.notAvailable.length,
    declined: grouped.declined.length,
    notDone: grouped.notDone.length,
  };

  const sections = CHECKLIST_SECTIONS.map(({ id, label }) => ({
    id,
    label,
    count: grouped[id].length,
    items: grouped[id],
  }));

  return {
    summary,
    sections,
    isToday,
    hasData: checklists.length > 0,
    date: apiData?.date || selectedDateKey,
  };
};

const emptyChecklistState = (selectedDateKey) => {
  const todayKey = getTodayDateKey();
  return {
    summary: {
      total: 0,
      completed: 0,
      notAvailable: 0,
      declined: 0,
      notDone: 0,
    },
    sections: CHECKLIST_SECTIONS.map(({ id, label }) => ({
      id,
      label,
      count: 0,
      items: [],
    })),
    isToday: selectedDateKey === todayKey,
    hasData: false,
    date: selectedDateKey,
  };
};

const pickDefaultChecklistTab = (summary) => {
  if (summary.completed > 0) {
    return "completed";
  }
  if (summary.notDone > 0) {
    return "notDone";
  }
  if (summary.notAvailable > 0) {
    return "notAvailable";
  }
  if (summary.declined > 0) {
    return "declined";
  }
  return "completed";
};

const STATUS_META = {
  completed: {
    icon: FaCheckCircle,
    badgeClass: "family-checklist-badge--completed",
    itemClass: "family-checklist-item--completed",
  },
  notAvailable: {
    icon: FaMinusCircle,
    badgeClass: "family-checklist-badge--not-available",
    itemClass: "family-checklist-item--not-available",
  },
  declined: {
    icon: FaTimesCircle,
    badgeClass: "family-checklist-badge--declined",
    itemClass: "family-checklist-item--declined",
  },
  notDone: {
    icon: FaTimesCircle,
    badgeClass: "family-checklist-badge--not-completed",
    itemClass: "family-checklist-item--not-completed",
  },
};

const ChecklistItemButton = ({ item, sectionId, meta, onSelect }) => {
  const showCaregiverMeta = (sectionId === "completed" || sectionId === "notAvailable" || sectionId === "declined") && (item.caregiverName || item.time);

  return (
    <li className={`family-checklist-item ${meta.itemClass}`}>
      <button type="button" className="family-checklist-item-button" onClick={() => onSelect(item)}>
        <div className="family-checklist-item-main">
          <span className="family-checklist-item-task">{item.task}</span>
          {showCaregiverMeta ? (
            <div className="family-checklist-item-meta">
              {item.caregiverName ? <span className="family-checklist-caregiver">{item.caregiverName}</span> : null}
              {item.time ? <span className="family-checklist-time">{item.time}</span> : null}
            </div>
          ) : null}
          {(sectionId === "notAvailable" || sectionId === "declined") && item.reason ? (
            <p className="family-checklist-reason">
              <span className="family-checklist-reason-label">Reason:</span> {item.reason}
            </p>
          ) : null}
        </div>
      </button>
    </li>
  );
};

const FamilyChecklist = () => {
  const navigate = useNavigate();
  const todayKey = getTodayDateKey();
  const [selectedDateKey, setSelectedDateKey] = useState(todayKey);
  const [activeFilter, setActiveFilter] = useState("completed");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [checklistData, setChecklistData] = useState(() => emptyChecklistState(todayKey));
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const dateInputRef = useRef(null);
  const listScrollRef = useRef(null);
  const useCustomDatePicker = shouldUseCustomDatePicker();

  const { familyClientId, familyMemberClientId } = getSessionContext();
  const clientId = familyClientId || familyMemberClientId;
  const { summary, sections, isToday, hasData } = checklistData;
  const dateLabel = formatDateLabel(selectedDateKey, { isToday });

  const activeSection = sections.find((section) => section.id === activeFilter);
  const activeMeta = activeSection ? STATUS_META[activeSection.id] : null;
  const ActiveSectionIcon = activeMeta?.icon;

  const resetToToday = () => {
    setSelectedDateKey(todayKey);
  };

  const openDatePicker = () => {
    if (useCustomDatePicker) {
      setShowCustomDatePicker(true);
      return;
    }

    const input = dateInputRef.current;
    if (!input) {
      setShowCustomDatePicker(true);
      return;
    }

    if (typeof input.showPicker === "function") {
      try {
        input.showPicker();
        return;
      } catch {
        setShowCustomDatePicker(true);
        return;
      }
    }

    input.click();
  };

  const handleDateChange = (event) => {
    const value = event.target.value;
    if (value) {
      setSelectedDateKey(value);
    }
  };

  const openTaskDetail = (task) => {
    setSelectedTask(task);
    setTaskDialogOpen(true);
  };

  const closeTaskDetail = () => {
    setTaskDialogOpen(false);
  };

  useEffect(() => {
    let cancelled = false;

    const fetchChecklist = async () => {
      if (!clientId) {
        setChecklistData(emptyChecklistState(selectedDateKey));
        setError("Please select a client to view checklist.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      const result = await getCheckListOfFamilyMember(clientId, selectedDateKey);
      if (cancelled) {
        return;
      }

      if (!result.success) {
        setChecklistData(emptyChecklistState(selectedDateKey));
        setError(result.message || "Failed to load checklist.");
        setLoading(false);
        return;
      }

      const mapped = mapChecklistApiResponse(result.data, selectedDateKey);
      setChecklistData(mapped);
      setActiveFilter(pickDefaultChecklistTab(mapped.summary));
      setLoading(false);
    };

    fetchChecklist();
    return () => {
      cancelled = true;
    };
  }, [clientId, selectedDateKey]);

  useEffect(() => {
    listScrollRef.current?.scrollTo(0, 0);
  }, [activeFilter, selectedDateKey]);

  return (
    <div className="family-page-layout family-checklist-layout">
      <FamilyHeader />
      <Backdrop sx={{ color: "#ffba30", zIndex: (theme) => theme.zIndex.drawer + 1000 }} open={loading}>
        <img src={require("../../assets/btTree.png")} className="loading-img" alt="" />
      </Backdrop>

      <div className="family-page-subheader">
        <div className="family-checklist-topbar">
          <button type="button" className="family-checklist-back" onClick={() => navigate("/family-home")} aria-label="Back to home">
            <IoChevronBack />
          </button>
          <h1 className="family-checklist-title-center">Checklist</h1>
          <div className="family-checklist-calendar-wrap">
            {!useCustomDatePicker ? <input ref={dateInputRef} type="date" className="family-checklist-date-input-hidden" value={selectedDateKey} max={todayKey} onChange={handleDateChange} tabIndex={-1} aria-hidden="true" /> : null}
            <button type="button" className={`family-checklist-calendar-btn ${!isToday ? "is-active" : ""}`} onClick={openDatePicker} aria-label="Open date picker">
              <IoCalendarOutline />
            </button>
          </div>
        </div>
      </div>

      <div className="family-checklist-page">
        <div className="family-checklist-toolbar">
          {!isToday ? (
            <div className="family-checklist-date-banner">
              <span>Showing checklist for {dateLabel}</span>
              <button type="button" className="family-checklist-reset-link" onClick={resetToToday}>
                Reset
              </button>
            </div>
          ) : null}

          {!loading && error ? (
            <div className="family-checklist-empty-date">
              <p>{error}</p>
            </div>
          ) : null}

          {!loading && !error && hasData ? (
            <div className="family-checklist-summary-card">
              <div className="family-checklist-summary-head">
                <span className="family-checklist-date">{dateLabel}</span>
                <span className="family-checklist-summary-total">{summary.total} tasks</span>
              </div>
              <div className="family-checklist-stats" role="tablist" aria-label="Checklist categories">
                <button type="button" role="tab" aria-selected={activeFilter === "completed"} className={`family-checklist-stat family-checklist-stat--completed ${activeFilter === "completed" ? "is-active" : ""}`} onClick={() => setActiveFilter("completed")} disabled={summary.completed === 0}>
                  <span className="family-checklist-stat-value">{summary.completed}</span>
                  <span className="family-checklist-stat-label">Completed</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeFilter === "notAvailable"}
                  className={`family-checklist-stat family-checklist-stat--not-available ${activeFilter === "notAvailable" ? "is-active" : ""}`}
                  onClick={() => setActiveFilter("notAvailable")}
                  disabled={summary.notAvailable === 0}>
                  <span className="family-checklist-stat-value">{summary.notAvailable}</span>
                  <span className="family-checklist-stat-label">Not Available</span>
                </button>
                <button type="button" role="tab" aria-selected={activeFilter === "declined"} className={`family-checklist-stat family-checklist-stat--declined ${activeFilter === "declined" ? "is-active" : ""}`} onClick={() => setActiveFilter("declined")} disabled={summary.declined === 0}>
                  <span className="family-checklist-stat-value">{summary.declined}</span>
                  <span className="family-checklist-stat-label">Declined</span>
                </button>
                <button type="button" role="tab" aria-selected={activeFilter === "notDone"} className={`family-checklist-stat family-checklist-stat--not-completed ${activeFilter === "notDone" ? "is-active" : ""}`} onClick={() => setActiveFilter("notDone")} disabled={summary.notDone === 0}>
                  <span className="family-checklist-stat-value">{summary.notDone}</span>
                  <span className="family-checklist-stat-label">Not Done</span>
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <div className="family-checklist-body">
          {!loading && !error && hasData ? (
            activeSection && activeMeta && activeSection.count > 0 ? (
              <section className="family-checklist-section family-checklist-section--solo">
                <div className="family-checklist-section-header family-checklist-section-header--static">
                  <span className={`family-checklist-badge ${activeMeta.badgeClass}`}>
                    {ActiveSectionIcon ? <ActiveSectionIcon aria-hidden="true" /> : null}
                    {activeSection.label}
                  </span>
                </div>
                <div className="family-checklist-list-scroll" ref={listScrollRef}>
                  <ul className="family-checklist-list family-checklist-list--compact">
                    {activeSection.items.map((item) => (
                      <ChecklistItemButton key={item.id} item={item} sectionId={activeSection.id} meta={activeMeta} onSelect={openTaskDetail} />
                    ))}
                  </ul>
                </div>
              </section>
            ) : (
              <div className="family-checklist-empty-tab">No tasks in this category for the selected date.</div>
            )
          ) : null}

          {!loading && !error && !hasData ? (
            <div className="family-checklist-empty-date">
              <IoCalendarOutline className="family-checklist-empty-date-icon" aria-hidden="true" />
              <p>No checklist records for {dateLabel}.</p>
              <button type="button" className="family-checklist-reset-btn" onClick={resetToToday}>
                Back to today&apos;s list
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <FamilyChecklistDatePickerDialog open={showCustomDatePicker} onClose={() => setShowCustomDatePicker(false)} value={selectedDateKey} maxDate={todayKey} onChange={setSelectedDateKey} />

      <Dialog
        className="family-checklist-desc-dialog"
        open={taskDialogOpen}
        onClose={closeTaskDetail}
        maxWidth="xs"
        fullWidth
        TransitionProps={{
          onExited: () => setSelectedTask(null),
        }}
        PaperProps={{ className: "family-checklist-desc-dialog-paper" }}>
        <DialogContent className="family-modal-content family-checklist-desc-dialog-content">
          <div className="family-checklist-desc-dialog-header">
            <h2 className="family-modal-title" id="family-checklist-desc-title">
              Description
            </h2>
            <button type="button" className="family-modal-close" onClick={closeTaskDetail} aria-label="Close">
              <IoClose />
            </button>
          </div>
          <p className="family-checklist-desc-popup">{selectedTask?.description || "No description provided."}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FamilyChecklist;
